import React from "react"
import moment from "moment"

import Button from "../../../components/ui/button/button"

const toSeconds = (ts) => ts?.seconds ?? ts?._seconds ?? null

// Format a duration in seconds as e.g. "1h 23m" or "47m"
const formatDuration = (secs) => {
	const totalMinutes = Math.floor(secs / 60)
	const hours = Math.floor(totalMinutes / 60)
	const minutes = totalMinutes % 60
	if (hours <= 0) return `${minutes}m`
	return `${hours}h ${minutes}m`
}

// Single row for the Quoted Enquiries page
export default function Row({
	enquiry,
	usersById,
	client,
	quoteCount,
	firstQuoteSecs,
	firstOutboundSecs,
	onView,
}) {
	const createdSecs = toSeconds(enquiry.created)
	const dateFromSecs = toSeconds(enquiry.date_from)

	const enquiredAt = createdSecs ? moment(createdSecs, "X").format("MMM D[,] YYYY HH:mm") : null
	const travelDate = dateFromSecs ? moment(dateFromSecs, "X").format("MMM D[,] YYYY") : null

	let ageLabel = "—"
	if (createdSecs) {
		const createdMoment = moment(createdSecs, "X")
		const ageDays = moment().diff(createdMoment, "days")
		if (ageDays >= 1) {
			ageLabel = `${ageDays} ${ageDays === 1 ? "day" : "days"}`
		} else {
			const totalMinutes = moment().diff(createdMoment, "minutes")
			const hours = Math.floor(totalMinutes / 60)
			const minutes = totalMinutes % 60
			ageLabel = `${hours}h ${minutes}m`
		}
	}

	const agent = enquiry.agent ? usersById[enquiry.agent] : null
	const agentName = agent ? `${agent.first_name || ""} ${agent.last_name || ""}`.trim() : null

	const clientName = client ? `${client.first_name || ""} ${client.last_name || ""}`.trim() : null

	// Combined "time to first response" — whichever happened sooner between
	// the first quote being sent and the first outbound activity (email sent,
	// call made, voicemail left). Same thresholds and outside-hours exception
	// as the previous time-to-quote metric.
	const firstResponseCandidates = [firstQuoteSecs, firstOutboundSecs].filter(
		(secs) => secs != null && createdSecs != null && secs >= createdSecs,
	)
	const firstResponseSecs = firstResponseCandidates.length
		? Math.min(...firstResponseCandidates)
		: null

	let frLabel = null
	let frClass = ""
	let frOutsideHours = false
	if (firstResponseSecs && createdSecs) {
		const diffSecs = firstResponseSecs - createdSecs
		frLabel = formatDuration(diffSecs)
		// Office hours window is 09:00 – 17:30. Enquiries received outside of
		// that window are flagged as an exception rather than being colour
		// coded against the SLA thresholds.
		const enquiryMoment = moment(createdSecs, "X")
		const minutesIntoDay = enquiryMoment.hour() * 60 + enquiryMoment.minute()
		const isBeforeHours = minutesIntoDay < 9 * 60
		const isAfterHours = minutesIntoDay >= 17 * 60 + 30
		if (isBeforeHours || isAfterHours) {
			frOutsideHours = true
		} else {
			const diffMinutes = diffSecs / 60
			if (diffMinutes > 120) frClass = "ttq-red"
			else if (diffMinutes > 90) frClass = "ttq-amber"
			else frClass = "ttq-green"
		}
	}

	return (
		<tr data-system-id={enquiry.id}>
			<td>{enquiredAt || <small className="no-value">No date</small>}</td>
			<td>{agentName || <small className="no-value">Unassigned</small>}</td>
			<td>{clientName || <small className="no-value">—</small>}</td>
			<td>{enquiry.group_size || <small className="no-value">—</small>}</td>
			<td>{enquiry.location || <small className="no-value">—</small>}</td>
			<td>{travelDate || <small className="no-value">—</small>}</td>
			<td>
				{firstResponseSecs ? (
					<small className="no-value">—</small>
				) : (
					ageLabel
				)}
			</td>
			<td>{quoteCount > 0 ? quoteCount : <small className="no-value">—</small>}</td>
			<td>
				{frLabel ? (
					<span className={frClass}>
						{frLabel}
						{frOutsideHours && " (exception)"}
					</span>
				) : (
					<small className="no-value">—</small>
				)}
			</td>
			<td
				className="is-button"
				style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
				<Button
					small={true}
					label="View enquiry"
					onClick={() => onView(enquiry.id)}
				/>
			</td>
		</tr>
	)
}
